import { useEffect, useState } from "react";
import { useListContext } from "react-admin";
import { MenuItem } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";

//Common styles
import {
  FilterContainer,
  FilterInput,
  FilterSection,
  FilterTextField,
  FilterWrapper,
  FIlterHeader,
  SectionHeader,
  SectionTitle,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { FilterIcon } from "../Svg";
import TransactionButton from "./TransactionButton";

export interface CommonFilterOption {
  value: string | number;
  label: string;
}

export interface CommonFilterField {
  /**
   * - text: debounced free-text input
   * - select: single-choice dropdown with an "All" option
   * - multiselect: multi-choice dropdown
   * - date: date picker, stored as "YYYY-MM-DD"
   */
  type: "text" | "select" | "multiselect" | "date";
  label: string;
  /** Filter key sent to the backend (react-admin filterValues key) */
  source: string;
  /** Choices for select / multiselect */
  options?: CommonFilterOption[];
  /** Restrict a text input to digits */
  numeric?: boolean;
  minWidth?: number;
  /** How many grid columns this field spans (only meaningful when <CommonFilterCard columns={N}> is used) */
  span?: number;
  /** Convert the input value before writing it to the filter (e.g. "true" -> true) */
  toFilter?: (value: any) => any;
  /** Convert the stored filter value back to the input value */
  fromFilter?: (value: any) => any;
}

/**
 * PendingCarts-style filter card that plugs into any react-admin <List>.
 * Reads/writes the list's filterValues, so the dataProvider query keeps
 * working exactly as with react-admin's own FilterForm.
 */
const CommonFilterCard = ({ fields, columns }: { fields: CommonFilterField[]; columns?: number }) => {
  const { filterValues, setFilters, isLoading } = useListContext();

  const textFields = fields.filter((field) => field.type === "text");
  const textSources = textFields.map((field) => field.source);

  const [textValues, setTextValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      textFields.map((field) => [
        field.source,
        String(filterValues[field.source] ?? ""),
      ])
    )
  );

  const externalTextValues = textSources
    .map((source) => String(filterValues[source] ?? ""))
    .join("");

  // Keep text inputs in sync when filters are cleared/changed elsewhere
  useEffect(() => {
    setTextValues(
      Object.fromEntries(
        textSources.map((source) => [
          source,
          String(filterValues[source] ?? ""),
        ])
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTextValues]);

  const applyFilter = (field: CommonFilterField, inputValue: any) => {
    const next: any = { ...filterValues };
    const isEmpty =
      inputValue === "" ||
      inputValue === null ||
      inputValue === undefined ||
      (Array.isArray(inputValue) && inputValue.length === 0);
    if (isEmpty) {
      delete next[field.source];
    } else {
      next[field.source] = field.toFilter ? field.toFilter(inputValue) : inputValue;
    }
    setFilters(next, {}, false);
  };

  // Debounce the text inputs
  useEffect(() => {
    const timer = setTimeout(() => {
      const changed = textFields.some(
        (field) =>
          String(filterValues[field.source] ?? "") !==
          (textValues[field.source] ?? "")
      );
      if (!changed) return;

      const next: any = { ...filterValues };
      textFields.forEach((field) => {
        const value = textValues[field.source];
        if (value === "" || value === null || value === undefined) {
          delete next[field.source];
        } else {
          next[field.source] = field.toFilter ? field.toFilter(value) : value;
        }
      });
      setFilters(next, {}, false);
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(textValues)]);

  const renderField = (field: CommonFilterField) => {
    switch (field.type) {
      case "text":
        return (
          <FilterInput
            key={field.source}
            value={textValues[field.source] ?? ""}
            size="small"
            variant="outlined"
            label={field.label}
            autoComplete="off"
            onChange={(event) => {
              const value = event.target.value;
              if (field.numeric && value !== "" && !/^\d+$/.test(value)) {
                return;
              }
              setTextValues((prev) => ({ ...prev, [field.source]: value }));
            }}
            sx={field.minWidth ? { minWidth: field.minWidth } : undefined}
          />
        );

      case "date": {
        const raw = filterValues[field.source];
        return (
          <DatePicker
            key={field.source}
            label={field.label}
            disableMaskedInput
            value={raw ? moment(raw, "YYYY-MM-DD").toDate() : null}
            inputFormat="dd/MM/yyyy"
            disabled={isLoading}
            onChange={(value) =>
              applyFilter(
                field,
                value ? moment(value).format("YYYY-MM-DD") : ""
              )
            }
            renderInput={(params) => (
              <FilterTextField {...params} size="small" variant="outlined" sx={{ minWidth: field.minWidth ?? 180 }} />
            )}
          />
        );
      }

      case "multiselect": {
        const value = Array.isArray(filterValues[field.source])
          ? filterValues[field.source]
          : [];
        return (
          <FilterTextField
            key={field.source}
            select
            label={field.label}
            size="small"
            variant="outlined"
            value={value}
            disabled={isLoading}
            onChange={(e) => applyFilter(field, e.target.value)}
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) => (selected as any[]).join(", "),
            }}
            sx={{ minWidth: columns ? 0 : (field.minWidth ?? 200) }}
          >
            {(field.options ?? []).map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FilterTextField>
        );
      }

      case "select":
      default: {
        const raw = filterValues[field.source];
        const value =
          raw === undefined || raw === null
            ? ""
            : field.fromFilter
            ? field.fromFilter(raw)
            : raw;
        return (
          <FilterTextField
            key={field.source}
            select
            label={field.label}
            size="small"
            variant="outlined"
            value={value}
            disabled={isLoading}
            onChange={(e) => applyFilter(field, e.target.value)}
            sx={{ minWidth: columns ? 0 : (field.minWidth ?? 180) }}
          >
            <MenuItem value="">All</MenuItem>
            {(field.options ?? []).map((option) => (
              <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FilterTextField>
        );
      }
    }
  };

  return (
    <FilterWrapper>
      <FIlterHeader>
        <FilterSection style={{ marginBottom: "0" }}>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <FilterIcon color="#1976d2" height={20} width={20} />
              <SectionTitle>Filters</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
          <TransactionButton
            name="Clear Filter"
            onClickHandler={() => setFilters({}, {}, false)}
          />
        </FilterSection>
      </FIlterHeader>
      <FilterContainer
        style={
          columns
            ? {
                display: "grid",
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                rowGap: "8px",
              }
            : { flexWrap: "wrap", rowGap: "8px" }
        }
      >
        {fields.map((field) => {
          const el = renderField(field);
          if (columns && field.span && field.span > 1) {
            return (
              <div key={field.source} style={{ gridColumn: `span ${field.span}` }}>
                {el}
              </div>
            );
          }
          return el;
        })}
      </FilterContainer>
    </FilterWrapper>
  );
};

export default CommonFilterCard;
